import { TRadiusType } from '@lifk/types'
import { groupMulti } from '@lifk/utils'
import { ExpressionSpecification } from 'mapbox-gl'

const defaultRadius = '$high-density'

// These breakpoints mark an ideal viewing zoom level for points.
// At these zoom levels (and higher), points match the design specs.
const highDensityBreakpoint = 16
const lowDensityBreakpoint = 14

export const variantMapping: Record<
  TRadiusType,
  {
    radius: ExpressionSpecification
    borderWidth: ExpressionSpecification
    imageSize?: ExpressionSpecification
  }
> = {
  '$high-density': {
    radius: [
      'interpolate',
      ['linear'],
      ['zoom'],
      8,
      1, // min size: 2px x 2px
      highDensityBreakpoint,
      5 // design specs: 10px x 10px
    ],
    borderWidth: [
      'interpolate',
      ['linear'],
      ['zoom'],
      13, // lower than zoom 13, no border
      0,
      highDensityBreakpoint,
      1.5
    ]
  },
  '$low-density': {
    radius: [
      'interpolate',
      ['linear'],
      ['zoom'],
      8,
      2, // min size: 4px x 4px
      lowDensityBreakpoint,
      10 // design specs: 20px x 20px
    ],
    borderWidth: [
      'interpolate',
      ['linear'],
      ['zoom'],
      8,
      0,
      lowDensityBreakpoint,
      2
    ],
    imageSize: [
      'interpolate',
      ['linear'],
      ['zoom'],
      8,
      0.25, // min size
      lowDensityBreakpoint,
      0.5 // design specs: original image should be 40px square image * 0.5 = 20px in the UI
    ]
  }
}

export const getPointsStyleSpec = (
  config: {
    radius?: TRadiusType
    filter?: ExpressionSpecification
  }[]
): {
  radius: ExpressionSpecification
  borderWidth: ExpressionSpecification
} => {
  const defaultVariant = variantMapping[defaultRadius]

  let pointSpec: {
    radius: ExpressionSpecification
    borderWidth: ExpressionSpecification
  } = {
    radius: defaultVariant.radius,
    borderWidth: defaultVariant.borderWidth
  }

  const groupByRadius = groupMulti(config, ({ radius }) => [
    radius ?? defaultRadius
  ])
  const radii = Object.keys(groupByRadius) as TRadiusType[]

  if (radii.length === 1) {
    pointSpec = variantMapping[radii[0]]
  } else if (radii.length > 1) {
    const radiusExpression: ExpressionSpecification = ['case']
    const borderWidthExpression: ExpressionSpecification = ['case']
    config.forEach(({ filter, radius }) => {
      if (filter) {
        const variant = variantMapping[radius ?? defaultRadius]

        radiusExpression.push(filter)
        radiusExpression.push(variant.radius)

        borderWidthExpression.push(filter)
        borderWidthExpression.push(variant.borderWidth)
      }
    })

    // default fallback for case statement
    radiusExpression.push(defaultVariant.radius)
    borderWidthExpression.push(defaultVariant.borderWidth)

    pointSpec.radius = radiusExpression
    pointSpec.borderWidth = borderWidthExpression
  }

  return pointSpec
}

export const getPointsImageSpec = (
  config: {
    radius?: TRadiusType
    filter?: ExpressionSpecification
    imageId?: string
  }[]
):
  | {
      'icon-image': ExpressionSpecification | string
      'icon-size': ExpressionSpecification
    }
  | undefined => {
  // NOTE: hardcoded $low-density here, since that is the only variant that supports images
  const uniqueImageIds = new Set<string>(
    config
      .filter(({ imageId, radius }) => imageId && radius === '$low-density')
      .map(({ imageId }) => imageId!)
  )

  let iconImage: ExpressionSpecification | string | undefined

  if (uniqueImageIds.size === 1) {
    iconImage = uniqueImageIds.values().next().value // get first value
  } else if (uniqueImageIds.size > 1) {
    const imageIdExpression: ExpressionSpecification = ['case']

    config.forEach(({ filter, imageId, radius }) => {
      // only show image if the variant supports it (only $low-density)
      const showImage = !!variantMapping[radius ?? defaultRadius].imageSize

      if (filter && imageId && showImage) {
        imageIdExpression.push(filter)
        imageIdExpression.push(imageId)
      }
    })
    imageIdExpression.push('') // default no image

    iconImage = imageIdExpression
  }

  if (iconImage) {
    return {
      'icon-size': variantMapping['$low-density']
        .imageSize as ExpressionSpecification,
      'icon-image': iconImage
    }
  }
  return undefined
}
